import { lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider, useAuth } from '@/contexts/AuthContext';
import { TenantProvider } from '@/contexts/TenantContext';

// Layouts - not lazy loaded (needed immediately)
import RoleBasedLayout from '@/layouts/RoleBasedLayout';

// Lazy loaded pages
const Login = lazy(() => import('@/pages/Login'));

// Admin Pages - lazy loaded
const Dashboard = lazy(() => import('@/pages/admin/Dashboard'));
const Customers = lazy(() => import('@/pages/admin/Customers'));
const Products = lazy(() => import('@/pages/admin/Products'));
const Invoices = lazy(() => import('@/pages/admin/Invoices'));
const Quotations = lazy(() => import('@/pages/admin/Quotations'));
const Payments = lazy(() => import('@/pages/admin/Payments'));
const Inventory = lazy(() => import('@/pages/admin/Inventory'));
const RoutesPage = lazy(() => import('@/pages/admin/Routes'));
const Staff = lazy(() => import('@/pages/admin/Staff'));
const StaffDetail = lazy(() => import('@/pages/admin/StaffDetail'));
const Expenses = lazy(() => import('@/pages/admin/Expenses'));
const Settings = lazy(() => import('@/pages/admin/Settings'));

// Sales Pages - lazy loaded
const SalesHome = lazy(() => import('@/pages/sales/Home'));
const CustomerDetail = lazy(() => import('@/pages/sales/CustomerDetail'));
const NewDelivery = lazy(() => import('@/pages/sales/NewDelivery'));
const CollectPayment = lazy(() => import('@/pages/sales/CollectPayment'));
const SalesPayments = lazy(() => import('@/pages/sales/SalesPayments'));
const TransactionHistory = lazy(() => import('@/pages/sales/TransactionHistory'));
const MyInventory = lazy(() => import('@/pages/sales/MyInventory'));

// Loading fallback component
function PageLoader() {
  return (
    <div className="min-h-[200px] flex items-center justify-center">
      <div className="spinner" />
    </div>
  );
}

// Create query client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30 * 1000, // 30 seconds
      retry: 1,
    },
  },
});

// Protected route wrapper
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="spinner" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
}

// Admin-only route guard
function AdminRoute({ children }: { children: React.ReactNode }) {
  const { isAdmin, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="spinner" />
      </div>
    );
  }

  if (!isAdmin) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
}

// Sales-only route guard
function SalesRoute({ children }: { children: React.ReactNode }) {
  const { isSalesStaff, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="spinner" />
      </div>
    );
  }

  if (!isSalesStaff) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
}

// Role-based home component
function RoleBasedHome() {
  const { isAdmin } = useAuth();
  return isAdmin ? <Dashboard /> : <SalesHome />;
}

function AppRoutes() {
  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        {/* Public routes */}
        <Route path="/login" element={<Login />} />

      {/* Protected routes */}
      <Route
        element={
          <ProtectedRoute>
            <RoleBasedLayout />
          </ProtectedRoute>
        }
      >
        {/* Home - different component per role */}
        <Route path="/" element={<RoleBasedHome />} />

        {/* Admin-only routes */}
        <Route
          path="/customers"
          element={
            <AdminRoute>
              <Customers />
            </AdminRoute>
          }
        />
        <Route
          path="/products"
          element={
            <AdminRoute>
              <Products />
            </AdminRoute>
          }
        />
        <Route
          path="/invoices"
          element={
            <AdminRoute>
              <Invoices />
            </AdminRoute>
          }
        />
        <Route
          path="/quotations"
          element={
            <AdminRoute>
              <Quotations />
            </AdminRoute>
          }
        />
        <Route
          path="/payments"
          element={
            <AdminRoute>
              <Payments />
            </AdminRoute>
          }
        />
        <Route
          path="/inventory"
          element={
            <AdminRoute>
              <Inventory />
            </AdminRoute>
          }
        />
        <Route
          path="/routes"
          element={
            <AdminRoute>
              <RoutesPage />
            </AdminRoute>
          }
        />
        <Route
          path="/staff"
          element={
            <AdminRoute>
              <Staff />
            </AdminRoute>
          }
        />
        <Route
          path="/staff/:staffId"
          element={
            <AdminRoute>
              <StaffDetail />
            </AdminRoute>
          }
        />
        <Route
          path="/expenses"
          element={
            <AdminRoute>
              <Expenses />
            </AdminRoute>
          }
        />
        <Route
          path="/settings"
          element={
            <AdminRoute>
              <Settings />
            </AdminRoute>
          }
        />

        {/* Sales staff routes */}
        <Route
          path="/my-inventory"
          element={
            <SalesRoute>
              <MyInventory />
            </SalesRoute>
          }
        />
        <Route
          path="/customer/:customerId"
          element={
            <SalesRoute>
              <CustomerDetail />
            </SalesRoute>
          }
        />
        <Route
          path="/customer/:customerId/delivery"
          element={
            <SalesRoute>
              <NewDelivery />
            </SalesRoute>
          }
        />
        <Route
          path="/customer/:customerId/payment"
          element={
            <SalesRoute>
              <CollectPayment />
            </SalesRoute>
          }
        />
        <Route
          path="/customer/:customerId/sales"
          element={
            <SalesRoute>
              <SalesPayments />
            </SalesRoute>
          }
        />
        <Route
          path="/customer/:customerId/history"
          element={
            <SalesRoute>
              <TransactionHistory />
            </SalesRoute>
          }
        />
      </Route>

        {/* Fallback */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
  );
}

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter
        future={{
          v7_startTransition: true,
          v7_relativeSplatPath: true,
        }}
      >
        <TenantProvider>
          <AuthProvider>
            <AppRoutes />
          </AuthProvider>
        </TenantProvider>
      </BrowserRouter>
    </QueryClientProvider>
  );
}
